import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authAPI, getToken, hydrateUserSession, isAuthenticated, logout, persistSession } from '../lib/auth';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];
const KEEP_ALIVE_INTERVAL_MS = 60000;
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export function useSessionKeepAlive() {
  const location = useLocation();
  const navigate = useNavigate();
  const lastActivityAt = useRef(Date.now());
  const timedOut = useRef(false);

  useEffect(() => {
    if (location.pathname === '/login') return;
    if (!isAuthenticated()) return;

    timedOut.current = false;
    lastActivityAt.current = Date.now();
    void hydrateUserSession();

    const markActivity = () => {
      lastActivityAt.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });

    const forceTimeout = () => {
      if (timedOut.current) return;
      timedOut.current = true;
      logout();
      navigate('/login?reason=timeout', { replace: true });
      window.setTimeout(() => {
        if (window.location.pathname !== '/login') {
          window.location.assign('/login?reason=timeout');
        }
      }, 50);
    };

    const pingKeepAlive = async () => {
      if (timedOut.current || document.visibilityState === 'hidden') return;
      if (Date.now() - lastActivityAt.current >= IDLE_TIMEOUT_MS) {
        forceTimeout();
        return;
      }
      if (!getToken()) return;
      try {
        const res = await authAPI.keepAlive();
        const token = res?.data?.access_token;
        const user = res?.data?.user;
        if (token) persistSession(token, user);
      } catch {
        // ignore
      }
    };

    const idleWatcher = window.setInterval(() => {
      if (Date.now() - lastActivityAt.current >= IDLE_TIMEOUT_MS) forceTimeout();
    }, 5000);

    void pingKeepAlive();
    const keepAliveTimer = window.setInterval(() => {
      void pingKeepAlive();
    }, KEEP_ALIVE_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      window.clearInterval(idleWatcher);
      window.clearInterval(keepAliveTimer);
    };
  }, [location.pathname, navigate]);
}

export default useSessionKeepAlive;
